import { BUILDING_DEFS, MAP_H, MAP_W, POWER_LINK_RANGE, colOf, inBounds } from '../constants';
import type { Simulation } from '../sim';
import { FLAG_CABLE, type Building, type SimState, type Terrain } from '../types';

interface PowerNode {
  building: Building;
  cx: number;
  cy: number;
  supply: number;
  draw: number;
}

/** Connected components of buried-cable tiles (4-neighbor BFS). */
function cableComponents(terrain: Terrain): Map<number, number> {
  const component = new Map<number, number>();
  let next = 0;
  for (let col = 0; col < MAP_W * MAP_H; col += 1) {
    if (((terrain.flags[col] ?? 0) & FLAG_CABLE) === 0 || component.has(col)) continue;
    const id = next++;
    const queue = [col];
    component.set(col, id);
    while (queue.length > 0) {
      const current = queue.pop()!;
      const x = current % MAP_W;
      const y = Math.floor(current / MAP_W);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        if (!inBounds(x + dx, y + dy)) continue;
        const neighbor = colOf(x + dx, y + dy);
        if (((terrain.flags[neighbor] ?? 0) & FLAG_CABLE) !== 0 && !component.has(neighbor)) {
          component.set(neighbor, id);
          queue.push(neighbor);
        }
      }
    }
  }
  return component;
}

/** Cable components a building touches: any cable under its footprint or on
 * the surrounding ring — laying lines under buildings is how they connect. */
function touchedCables(building: Building, component: Map<number, number>, touched: Set<number>): void {
  const def = BUILDING_DEFS[building.type];
  for (let dy = -1; dy <= def.h; dy += 1) {
    for (let dx = -1; dx <= def.w; dx += 1) {
      const x = building.x + dx;
      const y = building.y + dy;
      if (!inBounds(x, y)) continue;
      const id = component.get(colOf(x, y));
      if (id !== undefined) touched.add(id);
    }
  }
}

/** What a supplier actually feeds the wires today. Diesel and solar just run;
 * a nuclear plant needs its crew, its cooling water, and no active scram. */
export function supplierSupply(building: Building, day: number): number {
  const def = BUILDING_DEFS[building.type];
  const supply = def.powerSupply ?? 0;
  if (supply === 0) return 0;
  if (building.type === 'nuclearPlant') {
    if (building.staffAssigned.length < def.staffRequired) return 0;
    if (!building.watered) return 0;
    if (building.outageUntilDay !== null && day < building.outageUntilDay) return 0;
  }
  return supply;
}

/**
 * Union-find over power nodes (generators, poles, consumers). Two nodes link
 * when their footprint centers are within Chebyshev POWER_LINK_RANGE. Each
 * component gets eff = min(1, supply/demand); brownouts scale capacity.
 * Also totals the surplus sellable through staffed grid interconnects.
 */
export function rebuildPowerNetwork(sim: Simulation): void {
  const state = sim.state;
  const nodes: PowerNode[] = [];
  for (const building of state.buildings) {
    const def = BUILDING_DEFS[building.type];
    const supply = supplierSupply(building, sim.day);
    const draw = def.powerDraw;
    const isNode = (def.powerSupply ?? 0) > 0 || draw > 0 || building.type === 'powerPole' || building.type === 'gridInterconnect';
    if (!isNode) continue;
    nodes.push({
      building,
      cx: building.x + def.w / 2,
      cy: building.y + def.h / 2,
      supply,
      draw,
    });
  }

  const parent = nodes.map((_, index) => index);
  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) root = parent[root]!;
    while (parent[index] !== root) {
      const next = parent[index]!;
      parent[index] = root;
      index = next;
    }
    return root;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };

  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      const a = nodes[i]!;
      const b = nodes[j]!;
      if (Math.max(Math.abs(a.cx - b.cx), Math.abs(a.cy - b.cy)) <= POWER_LINK_RANGE) union(i, j);
    }
  }

  // Buried cables conduct too: all nodes touching the same cable run join.
  const cables = cableComponents(state.terrain);
  if (cables.size > 0) {
    const firstNodeOfCable = new Map<number, number>();
    const touched = new Set<number>();
    for (let index = 0; index < nodes.length; index += 1) {
      touched.clear();
      touchedCables(nodes[index]!.building, cables, touched);
      for (const id of touched) {
        const first = firstNodeOfCable.get(id);
        if (first === undefined) firstNodeOfCable.set(id, index);
        else union(index, first);
      }
    }
  }

  const supplyByRoot = new Map<number, number>();
  const demandByRoot = new Map<number, number>();
  for (let index = 0; index < nodes.length; index += 1) {
    const root = find(index);
    supplyByRoot.set(root, (supplyByRoot.get(root) ?? 0) + nodes[index]!.supply);
    demandByRoot.set(root, (demandByRoot.get(root) ?? 0) + nodes[index]!.draw);
  }

  for (const building of state.buildings) {
    if (BUILDING_DEFS[building.type].powerDraw === 0) {
      building.powered = true;
      building.powerEff = 1;
    }
  }
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index]!;
    if (node.draw === 0) continue;
    const root = find(index);
    const supply = supplyByRoot.get(root) ?? 0;
    const demand = demandByRoot.get(root) ?? 0;
    const eff = supply <= 0 ? 0 : Math.min(1, supply / demand);
    node.building.powered = eff > 0;
    node.building.powerEff = eff;
  }

  // Surplus on any network with a staffed interconnect sells to the grid.
  const surplusRoots = new Set<number>();
  for (let index = 0; index < nodes.length; index += 1) {
    const building = nodes[index]!.building;
    if (building.type !== 'gridInterconnect') continue;
    const def = BUILDING_DEFS.gridInterconnect;
    if (building.staffAssigned.length < def.staffRequired) continue;
    surplusRoots.add(find(index));
  }
  let sellable = 0;
  for (const root of surplusRoots) {
    sellable += Math.max(0, (supplyByRoot.get(root) ?? 0) - (demandByRoot.get(root) ?? 0));
  }
  sim.powerSellableSurplus = sellable;

  updateFunctional(state);
}

/** A building operates iff powered (eff >= 0.5), watered, and staffed. */
export function updateFunctional(state: SimState): void {
  for (const building of state.buildings) {
    const def = BUILDING_DEFS[building.type];
    const powerOk = def.powerDraw === 0 || (building.powered && building.powerEff >= 0.5);
    const waterOk = def.waterPerDay === undefined || building.watered;
    const staffOk = def.staffRequired === 0 || building.staffAssigned.length >= def.staffRequired;
    building.functional = powerOk && waterOk && staffOk;
  }
}
